import { History, PowerOff, SearchX } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import { useExecucoesCaptura } from '@/lib/captura/api';
import { ROTULO_STATUS_EXECUCAO } from '@/lib/captura/textos';
import { useEstadoTabela, voltarAPrimeiraPagina } from '@/lib/estado-tabela';
import { useAtualizarUrl } from '@/lib/url';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { TabelaPaginada } from '@/ui/components/tabela-paginada';
import { estiloBotao } from '@/ui/primitives/estilos';
import { Select } from '@/ui/primitives/select';
import { type EstadoCaptura, statusExecucaoCapturaSchema } from '@fiscoai/contracts';

import { colunasExecucoes } from './colunas-execucoes';

const TABELA = {
  chave: 'captura-execucoes',
  campos: ['iniciadaEm'] as const,
  padrao: { ordenarPor: 'iniciadaEm', ordem: 'desc' },
} as const;

const TODOS = 'TODOS';
const OPCOES = [
  { valor: TODOS, rotulo: 'Todos os resultados' },
  ...statusExecucaoCapturaSchema.options.map((s) => ({
    valor: s,
    rotulo: ROTULO_STATUS_EXECUCAO[s],
  })),
];

function Vazio({ estado, filtrado }: { estado: EstadoCaptura; filtrado: boolean }) {
  if (filtrado)
    return (
      <EstadoVazio
        icone={<SearchX />}
        titulo="Nenhuma execução com este resultado"
        texto="Escolha outro resultado ou volte para todos."
      />
    );
  if (!estado.ativa)
    return (
      <EstadoVazio
        icone={<PowerOff />}
        titulo="A captura está desligada"
        texto="Ligue a captura na aba Captura SEFAZ da empresa para o FiscoAI buscar as notas sozinho."
        acao={
          <Link
            to={`/empresas/${estado.empresa.id}?aba=captura`}
            className={estiloBotao({ tamanho: 'sm' })}
          >
            Abrir a aba Captura SEFAZ
          </Link>
        }
      />
    );
  return (
    <EstadoVazio
      icone={<History />}
      titulo="A captura ainda não rodou"
      texto="A primeira busca traz até 90 dias de notas."
    />
  );
}

/** Histórico de execuções da empresa escolhida, paginado no servidor (ADR 0024). */
export function HistoricoExecucoes({ estado }: { estado: EstadoCaptura }) {
  const { params, atualizar } = useAtualizarUrl();
  const tabela = useEstadoTabela(TABELA);
  const bruto = params.get('resultado');
  const status = statusExecucaoCapturaSchema.safeParse(bruto).data;
  const filtros = {
    pagina: tabela.pagina,
    porPagina: tabela.porPagina,
    ordem: tabela.ordem,
    ...(status ? { status } : {}),
  };
  const lista = useExecucoesCaptura(estado.empresa.id, filtros);
  const colunas = useMemo(() => colunasExecucoes(estado.fuso), [estado.fuso]);

  return (
    <section aria-labelledby="titulo-historico" className="grid min-h-0 gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="grid gap-0.5">
          <h2 id="titulo-historico" className="text-lg font-semibold">
            Histórico de {estado.empresa.nome}
          </h2>
          <p className="text-sm text-texto-suave">
            Cada consulta à SEFAZ, da agenda ou disparada por alguém, com o que trouxe.
          </p>
        </div>
        <Select
          aria-label="Resultado da execução"
          className="w-56"
          opcoes={OPCOES}
          valor={status ?? TODOS}
          aoMudar={(v) => {
            atualizar((p) => {
              if (v === TODOS) p.delete('resultado');
              else p.set('resultado', v);
              voltarAPrimeiraPagina(p);
            });
          }}
        />
      </div>
      <TabelaPaginada
        rotulo="Execuções da captura"
        className="min-h-80"
        colunas={colunas}
        dados={lista.data?.items ?? []}
        total={lista.data?.total ?? 0}
        pagina={tabela.pagina}
        porPagina={tabela.porPagina}
        aoMudarPagina={tabela.irParaPagina}
        aoMudarPorPagina={tabela.mudarPorPagina}
        ordenacao={tabela.ordenacao}
        aoOrdenar={tabela.ordenar}
        idLinha={(e) => e.id}
        carregando={lista.isPending}
        atualizando={lista.isPlaceholderData}
        vazio={
          lista.isError ? (
            <EstadoVazio
              tom="perigo"
              icone={<History />}
              titulo="O histórico não carregou"
              texto="O servidor não respondeu. Confira a conexão e recarregue a página."
            />
          ) : (
            <Vazio estado={estado} filtrado={status !== undefined} />
          )
        }
      />
    </section>
  );
}
